import { SAFE_ZONE_Z_INDEX } from '@src/safe-zones/zIndex';
import { backgroundVideoSaveService } from '@src/services/BackgroundVideoSaveService';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { AspectRatio } from '../../types.ts/video';
import { resolveAspectRatioVariant } from '../../utility/aspectRatioVariant';
import { LogManager } from '../../utility/Logger';
import { COMPACT_TOOLTIP_STYLE } from '../common/tooltipStyles';
import { useOutsideAlerter } from '../transcript/useClickOutside';
import {
  type AspectRatioStatus,
  STATUS_CONFIG,
  StatusBadge,
  StatusSpinner,
} from './AspectRatioStatusBadge';
import {
  AspectDropDown,
  AspectItem,
  AspectItemContent,
  AspectRatioLogo,
  AspectRow,
  CaretDownIcon,
  DropdownIcon,
  HorizontalAspectIcon,
  SelectedAspectRatio,
  SquareAspectIcon,
  VerticalAspectIcon,
} from './AspectRatioUiPrimitives';
import { SaveOrDiscardModal } from './SaveOrDiscardModal';

const logger = LogManager.getLogger('AspectRatiosDropdown');

/**
 * Resolve the new/draft/final status of an aspect ratio from the currently
 * loaded video and its variant graph (parent + associated videos), mirroring
 * Content Studio's clip dimensions dropdown.
 *
 * - `publishing` — a render for this AR's variant is currently in flight
 * - `final` — a variant for this AR exists and is published (`videoFilePrimary`)
 * - `draft` — a variant exists but isn't published yet
 * - `new` — no variant for this AR exists yet (created on click)
 *
 * The backend renders each aspect ratio's variant concurrently (tracked in
 * `renderingVideoIds`), so more than one AR can be publishing at once — each
 * row resolves its own variant id against that list.
 */
function getAspectRatioStatus(
  aspectRatio: string,
  currentVideo: Parameters<typeof resolveAspectRatioVariant>[1],
  renderingVideoIds: readonly string[],
): AspectRatioStatus {
  const match = resolveAspectRatioVariant(aspectRatio, currentVideo);
  if (match?.id && renderingVideoIds.includes(match.id)) return 'publishing';
  if (!match) return 'new';
  return match.videoFilePrimary ? 'final' : 'draft';
}

const AspectRatiosDropdown = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const selectedAspectRatio =
    videoCreator.currentVideo?.aspectRatio ||
    videoCreator.getDefaultAspectRatio();
  const selectedStatus = getAspectRatioStatus(
    selectedAspectRatio,
    videoCreator.currentVideo,
    videoCreator.renderingVideoIds,
  );
  const [dropdownOpen, toggleDropdown] = useState<boolean>(false);
  const [aspectRatioToProceed, setAspectRatioToProceed] =
    useState<AspectRatio | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useOutsideAlerter(dropdownRef, () => {
    toggleDropdown(false);
  });

  const renderAspectSquare = () => {
    switch (selectedAspectRatio) {
      case AspectRatio.AR_16_9:
        return <HorizontalAspectIcon />;
      case AspectRatio.AR_1_1:
        return <SquareAspectIcon />;
      case AspectRatio.AR_9_16:
        return <VerticalAspectIcon />;
    }
  };

  const handleChange = async (aspectRatio: AspectRatio) => {
    setAspectRatioToProceed(null);
    const nowLoadVideoWithoutAspectRatioStart = performance.now();
    await videoCreator.loadVideoWithAspectRatio(aspectRatio);
    logger.info(
      `Change aspect ratio: step 2 - loadVideoWithAspectRatio took ${
        performance.now() - nowLoadVideoWithoutAspectRatioStart
      } ms.`,
    );
  };

  return (
    <Main
      data-testid="creator-studio-aspect-ratio"
      disabled={!videoCreator.isVideoCreatorReady}
      ref={dropdownRef}
    >
      <AspectItem
        onClick={() =>
          videoCreator.isVideoCreatorReady && toggleDropdown(!dropdownOpen)
        }
      >
        <AspectItemContent>
          <AspectRatioLogo>{renderAspectSquare()}</AspectRatioLogo>
          <SelectedAspectRatio>{selectedAspectRatio}</SelectedAspectRatio>
        </AspectItemContent>
        <StatusBadge status={selectedStatus}>
          {selectedStatus === 'publishing' && <StatusSpinner />}
          {STATUS_CONFIG[selectedStatus].text}
        </StatusBadge>
        <DropdownIcon isOpen={dropdownOpen}>
          <CaretDownIcon />
        </DropdownIcon>
      </AspectItem>
      {dropdownOpen && (
        <>
          <ArDropDown>
            {Object.entries(ASPECT_RATIO_ICONS).map(
              ([aspectRatio, aspectRatioComponent]) => {
                const AspectRatioIcon = aspectRatioComponent.Icon;
                const isSelected = selectedAspectRatio === aspectRatio;
                const rowStatus = getAspectRatioStatus(
                  aspectRatio,
                  videoCreator.currentVideo,
                  videoCreator.renderingVideoIds,
                );
                const tooltipId = `aspect-ratio-${aspectRatio}-tooltip`;
                return (
                  <AspectRow
                    key={aspectRatio}
                    isSelected={isSelected}
                    onClick={async () => {
                      toggleDropdown(false);
                      const nowInitStart = performance.now();
                      if (
                        !videoCreator.currentVideo?.id &&
                        !videoCreator.hasDefaultAspectRatio()
                      ) {
                        setAspectRatioToProceed(aspectRatio as AspectRatio);
                        return;
                      }
                      if (videoCreator.currentVideo?.id) {
                        // If background video doesn't have id yet, that means
                        // that newly switched video will have partial
                        // parentVideo data (without id). This will cause
                        // unexpected behavior during next save
                        // (addCurrentToParentVideos). Unsupported.

                        // Two-phase save: prepare (await) then background save
                        const preparedVideo =
                          await backgroundVideoSaveService.prepare(
                            videoCreator,
                          );
                        // Fire-and-forget actual save
                        backgroundVideoSaveService.save(
                          preparedVideo,
                          videoCreator,
                        );
                      } else {
                        await videoCreator.saveStoryAndVideo();
                      }
                      logger.info(
                        `Change aspect ratio: step 1 - prepare took ${
                          performance.now() - nowInitStart
                        } ms.`,
                      );
                      handleChange(aspectRatio as AspectRatio);
                    }}
                    data-tooltip-id={tooltipId}
                    data-tooltip-content={`Ideal for ${SOCIALS_BY_ASPECT_RATIO[
                      aspectRatio as AspectRatio
                    ].join(', ')}`}
                  >
                    <AspectRatioLogo>
                      <AspectRatioIcon isSelected={isSelected} />
                    </AspectRatioLogo>
                    <RowLabel>{`${aspectRatio} (${aspectRatioComponent.label})`}</RowLabel>
                    <StatusBadge status={rowStatus}>
                      {rowStatus === 'publishing' && <StatusSpinner />}
                      {STATUS_CONFIG[rowStatus].text}
                    </StatusBadge>
                    <Tooltip
                      id={tooltipId}
                      style={COMPACT_TOOLTIP_STYLE}
                      place="right"
                      positionStrategy="fixed"
                      noArrow
                    />
                  </AspectRow>
                );
              },
            )}
          </ArDropDown>
        </>
      )}
      {aspectRatioToProceed && (
        <SaveOrDiscardModal
          closeModal={() => setAspectRatioToProceed(null)}
          action={() => handleChange(aspectRatioToProceed)}
        />
      )}
    </Main>
  );
});

export default AspectRatiosDropdown;

const Main = styled.div<{ disabled: boolean }>`
  position: relative;
  /* Stack above the safe-zone preview overlay (portaled to document.body
     at SAFE_ZONE_Z_INDEX.overlay) so the dropdown panel and its trigger
     paint on top of the preview, not behind it. */
  z-index: ${SAFE_ZONE_Z_INDEX.dropdownPanel};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  cursor: ${props => (props.disabled ? 'wait' : 'pointer')};
`;

/** Widen the panel so the right-aligned status badges line up across rows. */
const ArDropDown = styled(AspectDropDown)`
  min-width: 200px;
`;

const RowLabel = styled.span`
  white-space: nowrap;
`;

const SOCIALS_BY_ASPECT_RATIO = {
  [AspectRatio.AR_16_9]: ['YouTube', 'Facebook (Feed)'],
  [AspectRatio.AR_9_16]: [
    'Instagram & Facebook (Reels)',
    'LinkedIn',
    'TikTok',
    'YouTube Shorts',
  ],
  [AspectRatio.AR_1_1]: ['Twitter (X)'],
};

const ASPECT_RATIO_ICONS = {
  [AspectRatio.AR_16_9]: {
    label: 'Wide',
    Icon: (props: { isSelected: boolean }) => (
      <HorizontalAspectIcon isSelected={props.isSelected} />
    ),
  },
  [AspectRatio.AR_9_16]: {
    label: 'Vertical',
    Icon: (props: { isSelected: boolean }) => (
      <VerticalAspectIcon isSelected={props.isSelected} />
    ),
  },
  [AspectRatio.AR_1_1]: {
    label: 'Square',
    Icon: (props: { isSelected: boolean }) => (
      <SquareAspectIcon isSelected={props.isSelected} />
    ),
  },
};
